/** Contrast helpers for platform theme text/surface pairs — general-purpose subset of the math
 * originally built for messenger's chat bubble theming (see
 * platforms/messenger/frontend/web/src/utils/chatThemeContrast.ts), ported here so every platform
 * sharing this package gets the same WCAG-AA-safe text-color guarantees and glass/glow color math
 * without duplicating the logic per platform. */

const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = '#1a1a2e';
const MIN_CONTRAST = 4.5;

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.trim().replace('#', '');
  if (!/^[0-9a-f]{3,8}$/i.test(raw)) return null;
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw.slice(0, 6);
  const n = Number.parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Precomputed rgba() string, not a CSS color-mix() — safe on iOS Safari < 16.4 and every other
 * engine, since the whole point is this gets embedded directly as a CSS custom property value. */
export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
}

/** Flatten fgHex-at-alpha over bgHex into the solid color it would visually read as — used so a
 * "glass" theme's contrast-safe text color is computed against what the surface actually looks
 * like once blended with what's behind it, not against the fully-opaque swatch color. */
export function blendHexColors(bgHex: string, fgHex: string, fgAlpha: number): string {
  const bg = hexToRgb(bgHex);
  const fg = hexToRgb(fgHex);
  if (!bg || !fg) return bgHex;
  const a = Math.max(0, Math.min(1, fgAlpha));
  const mix = (bgC: number, fgC: number) => Math.round(fgC * a + bgC * (1 - a));
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(bg.r, fg.r))}${toHex(mix(bg.g, fg.g))}${toHex(mix(bg.b, fg.b))}`;
}

export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLin(rgb.r) + 0.7152 * toLin(rgb.g) + 0.0722 * toLin(rgb.b);
}

export function isLightColor(hex?: string): boolean {
  if (!hex || hex.includes('gradient')) return false;
  return relativeLuminance(hex) > 0.58;
}

export function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Pick #ffffff or #1a1a2e (or a valid preferred color, if it clears the contrast floor) for
 * readable text on a solid surface color. */
export function pickReadableText(bgHex: string, preferred?: string): string {
  const bgLum = relativeLuminance(bgHex);
  const lightContrast = contrastRatio(bgLum, relativeLuminance(LIGHT_TEXT));
  const darkContrast = contrastRatio(bgLum, relativeLuminance(DARK_TEXT));
  const auto = lightContrast >= darkContrast ? LIGHT_TEXT : DARK_TEXT;

  if (preferred?.startsWith('#')) {
    const prefContrast = contrastRatio(bgLum, relativeLuminance(preferred));
    if (prefContrast >= MIN_CONTRAST) return preferred;
  }
  return auto;
}

/** Final guard — never return a color that fails WCAG contrast on the given surface. */
export function ensureReadableText(bgHex: string, preferred?: string): string {
  const picked = pickReadableText(bgHex, preferred);
  const bgLum = relativeLuminance(bgHex);
  const pickedContrast = contrastRatio(bgLum, relativeLuminance(picked));
  if (pickedContrast >= MIN_CONTRAST) return picked;
  return contrastRatio(bgLum, relativeLuminance(LIGHT_TEXT)) >= contrastRatio(bgLum, relativeLuminance(DARK_TEXT))
    ? LIGHT_TEXT
    : DARK_TEXT;
}

export function isLightText(hex: string): boolean {
  return relativeLuminance(hex) > 0.62;
}

export { MIN_CONTRAST };
